import { useMemo } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import {
  DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS,
  MEMBERSHIP_PLAN_SORT_OPTION_COUNT,
  MEMBERSHIP_PLAN_STATUS_FILTERS,
  MembershipPlanListPanel,
  type MembershipPlanFrequencyFilter,
  type MembershipPlanListFilters,
} from '@/components/organisms/pricebook/MembershipPlanListPanel';
import { MembershipPlanTableView } from '@/components/organisms/pricebook/MembershipPlanTableView';
import { BILLING_FREQUENCY_LABELS } from '@/components/organisms/pricebook/support/membership-plan-shared';

const NUMBER_FILTER_OPS = ['eq', 'gt', 'lt', 'gte', 'lte'] as const;
const FREQUENCY_FILTERS = ['all', ...Object.keys(BILLING_FREQUENCY_LABELS)] as const;

export const Route = createFileRoute('/_dashboard/dashboard/membership-plans')({
  validateSearch: (search: Record<string, unknown>): MembershipPlanListFilters => {
    const sort = Number(search.sort);
    return {
      q: typeof search.q === 'string' ? search.q : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.q,
      status: MEMBERSHIP_PLAN_STATUS_FILTERS.some((filter) => filter.value === search.status)
        ? (search.status as MembershipPlanListFilters['status'])
        : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.status,
      billingOp: NUMBER_FILTER_OPS.includes(
        search.billingOp as MembershipPlanListFilters['billingOp'],
      )
        ? (search.billingOp as MembershipPlanListFilters['billingOp'])
        : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.billingOp,
      billingValue:
        typeof search.billingValue === 'string'
          ? search.billingValue
          : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.billingValue,
      discountOp: NUMBER_FILTER_OPS.includes(
        search.discountOp as MembershipPlanListFilters['discountOp'],
      )
        ? (search.discountOp as MembershipPlanListFilters['discountOp'])
        : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.discountOp,
      discountValue:
        typeof search.discountValue === 'string'
          ? search.discountValue
          : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.discountValue,
      frequency: FREQUENCY_FILTERS.includes(search.frequency as MembershipPlanFrequencyFilter)
        ? (search.frequency as MembershipPlanFrequencyFilter)
        : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.frequency,
      sort:
        Number.isFinite(sort) && sort >= 0
          ? Math.min(Math.floor(sort), MEMBERSHIP_PLAN_SORT_OPTION_COUNT - 1)
          : DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.sort,
      dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
      dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
      updatedFrom: typeof search.updatedFrom === 'string' ? search.updatedFrom : undefined,
      updatedTo: typeof search.updatedTo === 'string' ? search.updatedTo : undefined,
    };
  },
  component: MembershipPlansLayout,
});

function cleanMembershipPlanSearch(next: Record<string, unknown>): MembershipPlanListFilters {
  if (!next.q) delete next.q;
  if (next.status === DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.status) delete next.status;
  if (next.billingOp === DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.billingOp) delete next.billingOp;
  if (!next.billingValue) delete next.billingValue;
  if (next.discountOp === DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.discountOp) delete next.discountOp;
  if (!next.discountValue) delete next.discountValue;
  if (next.frequency === DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.frequency) delete next.frequency;
  if (next.sort === DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS.sort) delete next.sort;
  if (!next.dateFrom) delete next.dateFrom;
  if (!next.dateTo) delete next.dateTo;
  if (!next.updatedFrom) delete next.updatedFrom;
  if (!next.updatedTo) delete next.updatedTo;
  return next as unknown as MembershipPlanListFilters;
}

function MembershipPlansLayout() {
  const { location } = useRouterState();
  const router = useRouter();
  const filters = Route.useSearch();

  const idMatch = location.pathname.match(/\/dashboard\/membership-plans\/([^/]+)/);
  const selectedId = idMatch && idMatch[1] !== 'new' ? idMatch[1] : undefined;
  const isNewRoute = location.pathname.includes('/dashboard/membership-plans/new');
  const isEditRoute = /\/dashboard\/membership-plans\/[^/]+\/edit/.test(location.pathname);
  const isFormMode = isNewRoute || isEditRoute;
  const hasChildRoute = !!selectedId || isNewRoute;
  const isIndexRoute = /^\/dashboard\/membership-plans\/?$/.test(location.pathname);

  function handleFiltersChange(patch: Partial<MembershipPlanListFilters>) {
    void router.navigate({
      search: (prev) => {
        const next = { ...prev, ...patch } as Record<string, unknown>;
        return cleanMembershipPlanSearch(next) as never;
      },
      replace: true,
    });
  }

  function handleNewPlan() {
    // @ts-expect-error — search required by TanStack Router v1.168
    void router.navigate({ to: '/dashboard/membership-plans/new' });
  }

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <MembershipPlansHeaderSlot
        hidden={isFormMode}
        showTitleFallback={!isFormMode && !selectedId}
        onNewPlan={handleNewPlan}
      />
      <EntityIndexLayout
        title="Membership Plans"
        storageKey="membership-plans-view"
        hasChildRoute={hasChildRoute}
        // @ts-expect-error — search required by TanStack Router v1.168
        onResetToIndex={() => void router.navigate({ to: '/dashboard/membership-plans' })}
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-[620px] sm:max-w-[620px]"
        hideListInSplit={isFormMode}
        fullPageFormInTable
        splitList={() => (
          <div className="flex h-full flex-col">
            <MembershipPlanListPanel
              selectedId={selectedId}
              onSelect={(id) =>
                // @ts-expect-error — search required by TanStack Router v1.168
                void router.navigate({ to: '/dashboard/membership-plans/$id', params: { id } })
              }
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <MembershipPlanTableView
            onView={(plan) =>
              plan.id &&
              openInSheet(
                () =>
                  // @ts-expect-error — search required by TanStack Router v1.168
                  void router.navigate({
                    to: '/dashboard/membership-plans/$id',
                    params: { id: plan.id! },
                  }),
              )
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden h-full flex flex-col">
            {isIndexRoute ? (
              <MembershipPlanListPanel
                selectedId={selectedId}
                onSelect={(id) =>
                  // @ts-expect-error — search required by TanStack Router v1.168
                  void router.navigate({ to: '/dashboard/membership-plans/$id', params: { id } })
                }
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />
    </WorkspaceShell>
  );
}

function MembershipPlansHeaderSlot({
  hidden,
  showTitleFallback,
  onNewPlan,
}: {
  hidden: boolean;
  showTitleFallback: boolean;
  onNewPlan: () => void;
}) {
  const newButton = useMemo(() => {
    if (hidden) return null;
    return (
      <Button size="sm" title="Add plan" onClick={onNewPlan}>
        <Plus size={14} />
        Add plan
      </Button>
    );
  }, [hidden, onNewPlan]);

  // Fallback franja title when no plan is selected (table view, or the brief
  // index moment before the desktop auto-redirect). When a plan is open the
  // detail page's own slot supplies the richer context, taking over this key.
  const contextHeader = useMemo(() => {
    if (!showTitleFallback) return null;
    return <h1 className="truncate text-base font-semibold">Membership Plans</h1>;
  }, [showTitleFallback]);

  useWorkspaceHeader({ newButton, contextHeader }, [newButton, contextHeader]);
  return null;
}
